import { HAZARD_CATEGORIES } from "./hazardCategories.generated";

export type HazardCategoryConflict = {
  classLabel: string;
  codes: string[];
  labels: string[];
};

type Row = (typeof HAZARD_CATEGORIES)[number];

const ROWS_BY_CODE = new Map<string, Row>(HAZARD_CATEGORIES.map((row) => [row.code, row]));
const SEED_ORDER = new Map<string, number>(HAZARD_CATEGORIES.map((row, i) => [row.code, i]));

const SKIN = { into: "SKIN_CORROSION", label: "Skin Corrosion / Irritation" };
const EYE = { into: "SERIOUS_EYE_DAMAGE", label: "Serious Eye Damage / Eye Irritation" };

/** GHS splits these into two classes, but a substance meets only one of each pair. */
const MERGED_CLASSES: Readonly<Record<string, { into: string; label: string }>> = {
  SKIN_CORROSION: SKIN,
  SKIN_IRRITATION: SKIN,
  SERIOUS_EYE_DAMAGE: EYE,
  EYE_IRRITATION: EYE,
};

/** Classified per target organ or effect, so their categories co-occur. */
const NON_EXCLUSIVE_CLASSES = new Set([
  "SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE",
  "SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_OR_PROLONGED_EXPOSURE",
]);

/** Additive to the class's tier rather than a tier of it. */
const ADDITIVE_CODES = new Set(["REPRODUCTIVE_TOXICITY_EFFECTS_ON_OR_VIA_LACTATION"]);

const FLAMMABLE_GAS_1A = "FLAMMABLE_GASES_CATEGORY_1A";
/** Flammable-gas flags imply Category 1A; flags of one family exclude each other. */
const FLAMMABLE_GAS_FLAG_FAMILY: Readonly<Record<string, string>> = {
  FLAMMABLE_GASES_PYROPHORIC_GAS_CATEGORY_1A: "PYROPHORIC",
  FLAMMABLE_GASES_CHEMICALLY_UNSTABLE_GAS_CATEGORY_1A_A: "UNSTABLE",
  FLAMMABLE_GASES_CHEMICALLY_UNSTABLE_GAS_CATEGORY_1A_B: "UNSTABLE",
};

function rootOf(row: Row): Row {
  let current = row;
  while (current.parentCode) {
    const parent = ROWS_BY_CODE.get(current.parentCode);
    if (!parent) break;
    current = parent;
  }
  return current;
}

/** The exclusive group a code belongs to, or null when it excludes nothing. */
function exclusiveClassOf(code: string): { code: string; label: string } | null {
  const row = ROWS_BY_CODE.get(code);
  if (!row || !row.selectable || ADDITIVE_CODES.has(code)) return null;
  const root = rootOf(row);
  if (root.code === code || NON_EXCLUSIVE_CLASSES.has(root.code)) return null;
  const merged = MERGED_CLASSES[root.code];
  return merged
    ? { code: merged.into, label: merged.label }
    : { code: root.code, label: root.label };
}

export function hazardCategoriesConflict(a: string, b: string): boolean {
  if (a === b) return false;
  const classA = exclusiveClassOf(a);
  const classB = exclusiveClassOf(b);
  if (!classA || !classB || classA.code !== classB.code) return false;
  const flagA = FLAMMABLE_GAS_FLAG_FAMILY[a];
  const flagB = FLAMMABLE_GAS_FLAG_FAMILY[b];
  if (flagA && flagB) return flagA === flagB;
  if (flagA) return b !== FLAMMABLE_GAS_1A;
  if (flagB) return a !== FLAMMABLE_GAS_1A;
  return true;
}

function bySeedOrder(a: string, b: string): number {
  return (SEED_ORDER.get(a) ?? 0) - (SEED_ORDER.get(b) ?? 0);
}

/** One entry per class holding categories a substance cannot meet together. */
export function findHazardCategoryConflicts(codes: readonly string[]): HazardCategoryConflict[] {
  const unique = [...new Set(codes)].sort(bySeedOrder);
  const byClass = new Map<string, { label: string; codes: string[] }>();
  for (const code of unique) {
    const group = exclusiveClassOf(code);
    if (!group || !unique.some((other) => hazardCategoriesConflict(code, other))) continue;
    const entry = byClass.get(group.code) ?? { label: group.label, codes: [] };
    entry.codes.push(code);
    byClass.set(group.code, entry);
  }
  return [...byClass.values()].map((entry) => ({
    classLabel: entry.label,
    codes: entry.codes,
    labels: entry.codes.map((code) => ROWS_BY_CODE.get(code)?.label ?? code),
  }));
}

/** `selected` plus `added`, dropping whatever `added` contradicts. */
export function withoutConflictsFor(selected: readonly string[], added: string): string[] {
  return [
    ...selected.filter((code) => code !== added && !hazardCategoriesConflict(code, added)),
    added,
  ];
}
